import { UrlsWebHelpers } from 'idt-ecommerce-api/util/urls-web.helpers';

import type { Category, CategoryMetadata, CategoryMetadataType } from './categories.models';
import type {
  StorefrontMenu, StorefrontMenuItem
} from 'idt-ecommerce-api/_api/api-navigation-storefront/api-navigation-storefront.models';
import type { EcommerceContext } from 'idt-ecommerce-api/context/context.models';


export class StorefrontMenuTransformer {

  public static categoriesFromStorefrontMenu(menu: StorefrontMenu, context: EcommerceContext): Category[] {
    let items = [...menu.items];

    if (context.config.brandSettings.reorderMenuItems) {
      items = context.config.brandSettings.reorderMenuItems(items);
    }

    return items.map((item) => this.categoryFromMenuItem(item, context));
  }

  public static categoryFromMenuItem(
    item: StorefrontMenuItem,
    context: EcommerceContext,
    categoryTreePath: string = ''
  ): Category {
    const id = item.id.toString();
    const metadata = this.parseMetadata(item);

    const sectionPath = metadata.isHiddenInSectionPath
      ? categoryTreePath
      : this.addCurrentSectionName(categoryTreePath, item.name);

    const urlWeb = UrlsWebHelpers.getCategoryUrl(context, id, sectionPath);
    const urlCms = UrlsWebHelpers.getCategoryCMSUrl(context, id, sectionPath);
    const urlSeo = (item.seo?.keyword && item.seo?.seoId != null)
      ? UrlsWebHelpers.getStorefrontCategorySeoUrl(
          context,
          item.seo.keyword,
          item.seo.seoId.toString(),
          id,
          item.seo.seoExtraInfo?.isCanonicalReference
        )
      : undefined;

    const redirectToCategoryId = item.targetedRedirects?.length > 0
      ? item.targetedRedirects[0].menuItemId.toString()
      : undefined;

    const category: Category = {
      id,
      metadata,
      name: item.name,
      shortDescription: item.shortDescription?.text,
      redirectToCategoryId,
      sectionPath,
      subcategories: [],
      urlWeb,
      urlCms,
      attachments: [],
      urlSeo,
      key: item.key,
      seoId: item.seo?.seoId?.toString(),
      section: item.section,
      isHighlighted: item.isHighLighted,
      contentType: item.content?.type,
      contentId: item.content?.id
    };

    category.subcategories = (item.children ?? []).map(
      (child) => this.categoryFromMenuItem(child, context, category.sectionPath)
    );

    return category;
  }

  private static parseMetadata(item: StorefrontMenuItem): CategoryMetadata {
    const parsedType = this.resolveContentType(item);

    const metadata: CategoryMetadata = {
      type: parsedType,
      isHiddenInSectionPath: item.isHidden,
      isCmsFake: false,
      isEmpty: false,
      isNew: false,
      isLastSizesShowed: false,
      hasLine: false,
      hasLink: false,
      hasSpace: false,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      _debug: { _key: item.key }
    };

    if (item.styles?.color) {
      metadata.color = item.styles.color;
    }

    if (isDefined(item.key)) {
      const key = item.key.toLowerCase();
      const matchColor = /(?:_on_color)([a-f0-9]+)/.exec(key);
      metadata.isLastSizesShowed = key.includes('_ls');

      metadata.hasLink = key.includes('_link');
      metadata.hasLine = key.includes('_line');
      metadata.hasSpace = key.includes('espace');
      metadata.isEmpty = key.includes('_empty');
      metadata.isNew = key.includes('tagnew');

      if (matchColor?.[1]) {
        metadata.color = `#${matchColor[1]}`;
      }

      metadata.isHiddenInSectionPath = item.isHidden || key.includes('_noseo');
      metadata.isCmsFake = key.includes('_cmsfake');
    }

    return metadata;
  }

  private static resolveContentType(item: StorefrontMenuItem): CategoryMetadataType {
    const type = item.content?.type?.toLowerCase();

    switch (type) {
      case 'grid':
        return 'grid';
      case 'lookbook':
        return 'lookbook';
      case 'giftcard':
        return 'giftcard';
      case 'redirection':
        return 'legacy-web-redirection';
      default:
        return 'unknown';
    }
  }

  private static addCurrentSectionName(categoryTreePath: string, sectionName: string): string {
    let sectionPath = categoryTreePath;

    sectionName = sectionName.toLowerCase().trim();
    sectionName = this.stripHtmlTags(sectionName);
    sectionName = sectionName.replace(/\s|\/|\\/g, '-');
    sectionName = encodeURIComponent(sectionName);

    if (sectionPath === '') {
      sectionPath += sectionName;
    } else {
      sectionPath += `/${sectionName}`;
    }

    return sectionPath;
  }

  private static stripHtmlTags(text: string): string {
    let result = '';
    let isInsideTag = false;
    for (const char of text) {
      if (char === '<') {
        isInsideTag = true;
      } else if (char === '>') {
        isInsideTag = false;
      } else if (!isInsideTag) {
        result += char;
      }
    }
    return result;
  }

}
